"use client";
import Card from "@/components/card";
import * as styles from "./styles.css";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import ContentContainer from "@/components/content-container";
import PageSection from "@/components/page-section";
import ButtonContainer from "@/components/button-container";
import Button from "@/components/button";

export default function SearchModule(props: any) {
  const searchParams = useSearchParams();
  const query = searchParams.get("query") ?? "";
  const pageNumber = Number(searchParams.get("page")) || 1;
  const [searchValue, setSearchValue] = useState("");
  const [searching, setSearching] = useState<boolean>();
  const [data, setData] = useState<any[]>([]);
  const [latestNews, setLatestNews] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const queryNews = async () => {
    try {
      const response = await fetch("/api/news", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          count: 0,
          sourceList: "post",
          endCount: 3,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const { moreData } = await response.json();

      if (moreData) {
        setLatestNews(moreData);
      }
    } catch (error) {
      console.error("Error during fetch:", error);
    }
  };

  const queryData = async () => {
    try {
      const response = await fetch("/api/search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query,
          resultStart: data && data.length === 0 ? 0 : (pageNumber - 1) * 5,
          resultEnd: pageNumber * 5,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const { results, count } = await response.json();

      if (results) {
        if (pageNumber === 1) {
          setData(results);
        } else {
          setData([...data, ...results]);
        }
        setCount(count);
        setLoading(false);
        setSearching(false);
      }
    } catch (error) {
      console.error("Error during fetch:", error);
    }
  };

  const createQueryString = useCallback(
    (value: string, queryValue: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("query", queryValue);
      params.set("page", value);

      return params.toString();
    },
    [searchParams]
  );

  const handleSubmit = (event: any) => {
    event.preventDefault();
    setSearching(true);
    window.history.pushState(
      null,
      "",
      `?${createQueryString("1", searchValue)}`
    );
  };

  useEffect(() => {
    if (query) {
      setSearching(true);
      setSearchValue(query);
      queryData();
    }

    queryNews();
  }, []);

  useEffect(() => {
    if ((pageNumber && query) || query) {
      queryData();
    }
    if (pageNumber > 1) {
      setLoading(true);
    }
  }, [pageNumber, query]);

  const loadMore = async (event: any) => {
    event.preventDefault();
    const pageNumberIncrement = pageNumber === 0 ? 2 : 1;

    window.history.pushState(
      null,
      "",
      `?${createQueryString((pageNumber + pageNumberIncrement).toString(), searchValue)}`
    );

    setLoading(true);
  };

  return (
    <PageSection backgroundColor="background-lighten-5">
      <ContentContainer layout="search">
        <h3 className={styles.heading}>Search{data && " Results"}</h3>
        <div className={styles.fieldContainer}>
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              name="search"
              placeholder="Search"
              aria-label="search"
              autoFocus={true}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </form>
        </div>
        {searching && searchValue !== "" && (
          <div className={styles.resultsContainer}>Searching...</div>
        )}
        {data.length > 0 && !searching && (
          <div className={styles.resultsContainer}>
            <h6 className={styles.headingResults}>
              {`Showing ${data.length}${count > 0 ? " of " : ""}${count} ${data.length > 1 ? "results" : "result"}`}
            </h6>

            <div className={styles.resultsCard}>
              {data.map((item: any, index: number) => (
                <Card
                  key={`${item}-${index}`}
                  title={item?.title ?? ""}
                  button={{
                    _type: "buttonObject",
                    buttonLabel: "Learn More",
                    buttonVariant: "tertiary",
                    buttonVisible: true,
                    buttonLink: item?.buttonLink,
                    linkType: "internal link",
                  }}
                  variant="small"
                  type={"article"}
                />
              ))}
            </div>
          </div>
        )}
        {data.length === 0 && query && !searching && (
          <div className={styles.resultsContainer}>No results</div>
        )}
        {data.length === 0 && latestNews.length > 0 && (
          <div className={styles.resultsContainer}>
            <h6 className={styles.headingResults}>Latest News</h6>

            <div className={styles.resultsCard}>
              {latestNews.map((item: any, index: number) => (
                <Card
                  key={`${item}-${index}`}
                  title={item?.title ?? ""}
                  button={{
                    _type: "buttonObject",
                    buttonLabel: "Learn More",
                    buttonVariant: "tertiary",
                    buttonVisible: true,
                    buttonLink: item?.buttonLink,
                    linkType: "internal link",
                  }}
                  variant="small"
                  type={"article"}
                />
              ))}
            </div>
          </div>
        )}

        {count > data.length && data.length > 0 && (
          <div className={styles.loadmore}>
            <ButtonContainer>
              <Button
                label={loading ? "Loading..." : "Load More"}
                style="secondary"
                size="large"
                onClick={(event) => loadMore(event)}
                disabled={loading}
              />
            </ButtonContainer>
          </div>
        )}
      </ContentContainer>
    </PageSection>
  );
}
